import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { setAuthToken } from '../api/auth';
import axios from 'axios';
import { User, AuthContextType } from '../types';
import { useConfig } from '../hooks/useConfig';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_KEY = 'txjet_user';
// Session timeout values are now configurable through database
const LAST_ACTIVITY_KEY = 'txjet_last_activity';
/** How often to refresh JWT while the user is active (sliding session). */
const TOKEN_RENEW_INTERVAL_MS = 4 * 60 * 1000;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { config } = useConfig();
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      setAuthToken(parsed.token);
      return parsed;
    }
    setAuthToken(null);
    return null;
  });
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(60);
  const [userSessionTimeout, setUserSessionTimeout] = useState<number | null>(null);
  
  // Refs for throttling and debouncing
  const lastActivityRef = useRef<number>(0);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const modalLockRef = useRef<boolean>(false);
  const lastTokenRenewalRef = useRef<number>(0);

  // Align idle timeout with server: user preference, else global SESSION_TIMEOUT_MINUTES from config (same as JWT)
  const parsedConfigTimeout = parseInt(String(config?.SESSION_TIMEOUT_MINUTES ?? '30'), 10);
  const fallbackSessionMinutes =
    Number.isFinite(parsedConfigTimeout) && parsedConfigTimeout > 0 ? parsedConfigTimeout : 30;
  const effectiveSessionTimeout = userSessionTimeout ?? fallbackSessionMinutes;
  const sessionDuration = effectiveSessionTimeout * 60 * 1000; // Convert minutes to milliseconds
  const rawWarningMs = config?.SESSION_WARNING_MINUTES
    ? parseFloat(String(config.SESSION_WARNING_MINUTES)) * 60 * 1000
    : 2 * 60 * 1000;
  const warningMs =
    Number.isFinite(rawWarningMs) && rawWarningMs > 0 ? rawWarningMs : 2 * 60 * 1000;
  // Never wider than the session itself (minus 1 minute) so the warning window always fits
  const warningDuration = Math.min(
    Math.max(60 * 1000, warningMs),
    Math.max(60 * 1000, sessionDuration - 60 * 1000)
  );

  // Throttled activity handler (only update once per second)
  const throttledUpdateActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current >= 1000) {
      // Throttle to once per second
      lastActivityRef.current = now;
      if (user) {
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
        if (showSessionTimeoutModal) {
          setShowSessionTimeoutModal(false);
        }
        // Sliding JWT: periodically refresh so active users are not cut off by token expiry
        if (
          now - lastTokenRenewalRef.current >= TOKEN_RENEW_INTERVAL_MS &&
          !modalLockRef.current
        ) {
          lastTokenRenewalRef.current = now;
          axios
            .post('/api/users/session/renew')
            .then((res) => {
              const newToken = res.data?.token;
              if (newToken) {
                setAuthToken(newToken);
                setUser((prev) => (prev ? { ...prev, token: newToken } : null));
              }
            })
            .catch(() => {
              /* ignore — e.g. offline; 401 handled by response interceptor */
            });
        }
      }
    }
  }, [user, showSessionTimeoutModal]);

  // Debounced activity handler
  const debouncedUpdateActivity = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    activityTimeoutRef.current = setTimeout(() => {
      throttledUpdateActivity();
    }, 100); // Debounce to 100ms
  }, [throttledUpdateActivity]);

  // Update last activity timestamp
  const updateLastActivity = useCallback(() => {
    if (user) {
      const now = Date.now();
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      lastActivityRef.current = now;
    }
  }, [user]);

  // Force logout
  const forceLogout = useCallback(() => {
    modalLockRef.current = false; // Unlock activity updates
    setUser(null);
    setAuthToken(null);
    setShowSessionTimeoutModal(false);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    
    // Clear all intervals and timeouts
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    window.location.href = '/login';
  }, []);

  // Check session timeout
  const checkSessionTimeout = useCallback(() => {
    if (!user) return;

    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) {
      updateLastActivity();
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - parseInt(lastActivity);
    const timeUntilExpiry = sessionDuration - timeSinceLastActivity;

    if (timeUntilExpiry <= 0) {
      // Session has expired - force logout immediately
      forceLogout();
    } else if (timeUntilExpiry <= warningDuration) {
      // Show warning modal or update existing one
      const remainingSeconds = Math.ceil(timeUntilExpiry / 1000);
      setSessionTimeRemaining(remainingSeconds);
      
      if (!showSessionTimeoutModal) {
        modalLockRef.current = true; // Lock activity updates
        setShowSessionTimeoutModal(true);
        
        // Focus the browser window to get user's attention
        window.focus();
        
        // Flash the browser tab title
        const originalTitle = document.title;
        let flashCount = 0;
        const flashInterval = setInterval(() => {
          document.title = flashCount % 2 === 0 ? '⚠️ SESSION TIMEOUT ⚠️' : originalTitle;
          flashCount++;
          if (flashCount >= 10) { // Flash 5 times
            clearInterval(flashInterval);
            document.title = '⚠️ SESSION TIMEOUT ⚠️';
          }
        }, 500);
        
        // Show browser notification if permission granted
        if (Notification.permission === 'granted') {
          new Notification('TxJet Scheduler - Session Timeout', {
            body: `Your session will expire in ${Math.ceil(remainingSeconds / 60)} minutes. Click to extend your session.`,
            icon: '/favicon.ico',
            requireInteraction: true
          }).onclick = () => {
            window.focus();
          };
        } else if (Notification.permission !== 'denied') {
          // Request permission for future notifications
          Notification.requestPermission();
        }
        
      }
    } else {
      // Hide modal if we're outside the warning period (but only if not locked)
      if (showSessionTimeoutModal && !modalLockRef.current) {
        setShowSessionTimeoutModal(false);
      }
    }
  }, [user, showSessionTimeoutModal, updateLastActivity, forceLogout, sessionDuration, warningDuration]);

  // Extend session
  const extendSession = useCallback(() => {
    modalLockRef.current = false; // Unlock activity updates
    updateLastActivity();
    setShowSessionTimeoutModal(false);

    // Restore original title
    document.title = 'TxJet Scheduler';

    lastTokenRenewalRef.current = Date.now();
    axios
      .post('/api/users/session/renew')
      .then((res) => {
        const newToken = res.data?.token;
        if (newToken) {
          setAuthToken(newToken);
          setUser((prev) => (prev ? { ...prev, token: newToken } : null));
        }
      })
      .catch(() => {
        /* non-fatal */
      });
  }, [updateLastActivity]);

  // Reload user preferences (call this when user updates their preferences)
  const reloadUserPreferences = useCallback(async () => {
    if (!user) return;
    
    try {
      const userId = user.user_id || user.id;
      const customizationRes = await axios.get('/api/customizations');
      const userPrefsRes = await axios.get(`/api/customizations/user/${userId}`);
      
      // Reload session timeout preference
      const sessionTimeoutCustomization = customizationRes.data.find((c: any) => c.name === 'Session Timeout');
      if (sessionTimeoutCustomization) {
        const userPref = userPrefsRes.data.find((p: any) => p.customization_id === sessionTimeoutCustomization.customization_id);
        
        if (userPref && userPref.value) {
          setUserSessionTimeout(parseInt(userPref.value));
        } else {
          setUserSessionTimeout(null);
        }
      }
    } catch (err) {
      // Silently fail preference reload
    }
  }, [user]);


  // Set up activity listeners with optimized event handling
  useEffect(() => {
    if (user) {
      updateLastActivity();

      // Use passive listeners and only track essential events
      const events = ['click', 'keydown', 'scroll'];
      
      const handleActivity = (event: Event) => {
        // Skip if the event is from the session timeout modal
        const target = event.target as Element;
        if (target && target.closest && target.closest('.session-timeout-modal')) {
          // Ignore activity inside the session timeout modal
          return;
        }
        
        // Skip activity updates when modal is locked or visible
        if (modalLockRef.current) {
          return;
        }
        
        debouncedUpdateActivity();
      };

      events.forEach(event => {
        document.addEventListener(event, handleActivity, { passive: true });
      });

      // Check session timeout every 5 seconds for more responsive countdown
      sessionCheckIntervalRef.current = setInterval(checkSessionTimeout, 5000);

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleActivity);
        });
        if (sessionCheckIntervalRef.current) {
          clearInterval(sessionCheckIntervalRef.current);
        }
        if (activityTimeoutRef.current) {
          clearTimeout(activityTimeoutRef.current);
        }
      };
    }
  }, [user, debouncedUpdateActivity, checkSessionTimeout, updateLastActivity]);

  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setAuthToken(user.token);
      updateLastActivity();
    } else {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      setAuthToken(null);
    }
  }, [user, updateLastActivity]);

  useEffect(() => {
    // On mount, set token from localStorage if present
    const stored = localStorage.getItem(USER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.token) setAuthToken(parsed.token);
    }
    // Set up axios request interceptor to always attach token
    const reqInterceptor = axios.interceptors.request.use(config => {
      const stored = localStorage.getItem(USER_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.token) {
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${parsed.token}`;
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(reqInterceptor);
  }, []);

  useEffect(() => {
    // Set up axios interceptor for 401 errors
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          setUser(null);
          setAuthToken(null);
          if (user) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    // Set Authorization header if user is present
    if (user && user.token) {
      setAuthToken(user.token);
    }
    return () => axios.interceptors.response.eject(interceptor);
  }, [user]);

  const login = async (username: string, password: string) => {
    const res = await axios.post('/api/users/login', { username, password });
    const { token, user, must_change_password } = res.data;
    
    setUser({ ...user, token });
    setMustChangePassword(!!must_change_password);
    
    // Load and apply user's preferences (display mode and session timeout)
    try {
      const userId = user.user_id || user.id;
      const customizationRes = await axios.get('/api/customizations');
      const userPrefsRes = await axios.get(`/api/customizations/user/${userId}`);
      
      // Load display mode preference
      const displayModeCustomization = customizationRes.data.find((c: any) => c.name === 'Display Mode');
      if (displayModeCustomization) {
        const userPref = userPrefsRes.data.find((p: any) => p.customization_id === displayModeCustomization.customization_id);
        
        if (userPref && userPref.value === 'dark') {
          document.documentElement.classList.add('dark');
          localStorage.setItem('darkMode', 'true');
        } else {
          document.documentElement.classList.remove('dark');
          localStorage.setItem('darkMode', 'false');
        }
      }
      
      // Load session timeout preference
      const sessionTimeoutCustomization = customizationRes.data.find((c: any) => c.name === 'Session Timeout');
      if (sessionTimeoutCustomization) {
        const userPref = userPrefsRes.data.find((p: any) => p.customization_id === sessionTimeoutCustomization.customization_id);
        
        if (userPref && userPref.value) {
          setUserSessionTimeout(parseInt(userPref.value));
        } else {
          // No user preference set, will use environment default
          setUserSessionTimeout(null);
        }
      }
    } catch (err) {
      // Don't fail login if preference loading fails
      setUserSessionTimeout(null);
    }
  };

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    
    // Clear all intervals and timeouts
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      mustChangePassword, 
      login, 
      logout, 
      setMustChangePassword,
      showSessionTimeoutModal,
      sessionTimeRemaining,
      extendSession,
      forceLogout,
      reloadUserPreferences
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}; 